import { create } from 'zustand';
import type { Team } from '@bracketblitz/shared-types';

// Re-export for convenience
export type { Team };

interface TeamsState {
  teamsById: Record<string, Team>;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  // Actions
  fetchTeams: (opts?: { force?: boolean }) => Promise<void>;
  getTeam: (id: string) => Team | undefined;
  getTeamsByRegion: (region: string) => Team[];
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const API_BASE = import.meta.env.VITE_API_URL || 'https://bracketblitz-production.up.railway.app/api/v1';

export const useTeamsStore = create<TeamsState>()((set, get) => ({
  teamsById: {},
  isLoading: false,
  error: null,
  lastFetchedAt: null,

  fetchTeams: async (opts) => {
    const { lastFetchedAt, isLoading } = get();

    // SWR: skip if recently fetched (unless forced)
    if (!opts?.force && lastFetchedAt && Date.now() - lastFetchedAt < CACHE_TTL) {
      return;
    }

    // Don't double-fetch
    if (isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const resp = await fetch(`${API_BASE}/teams`, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!resp.ok) {
        throw new Error(`Teams API returned ${resp.status}`);
      }

      let teams: Team[] = await resp.json();

      // Unwrap API envelope if present
      if (teams && typeof teams === 'object' && 'data' in teams && 'success' in teams) {
        teams = (teams as any).data;
      }

      const teamsById: Record<string, Team> = {};
      for (const team of teams) {
        if (team.id) teamsById[team.id] = team; // dedupes by id naturally
      }

      set({ teamsById, isLoading: false, lastFetchedAt: Date.now() });
    } catch (err: any) {
      console.error('Failed to fetch teams:', err);
      set({ isLoading: false, error: err.message });
    }
  },

  getTeam: (id: string) => {
    return get().teamsById[id];
  },

  getTeamsByRegion: (region: string) => {
    return Object.values(get().teamsById).filter(
      t => t.region?.toLowerCase() === region.toLowerCase()
    );
  },
}));
